import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, ChevronDown, Menu, NotebookPen } from 'lucide-react';
import { ThemeToggle } from '../ui/ThemeToggle';
import { GlobalSearch } from '../ui/GlobalSearch';
import { QuickCaptureModal } from '../notebook';
import { UserAvatar } from '../incidents/AssignmentPicker';
import { useUser } from '../../context';
import { cn } from '../../lib/utils';

interface TopBarProps {
  sidebarCollapsed: boolean;
  onMenuToggle: () => void;
}

export function TopBar({ sidebarCollapsed, onMenuToggle }: TopBarProps) {
  const { currentUser, swaUser, isLoading, logout } = useUser();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showQuickNote, setShowQuickNote] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const displayName = currentUser?.displayName || swaUser?.userDetails || 'Loading...';
  const userRole = currentUser?.role?.replace('_', ' ') || '';
  const userEmail = swaUser?.userDetails || currentUser?.email || '';

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
    <header
      className={cn(
        'fixed top-0 right-0 z-30 h-14 bg-bg-primary/80 backdrop-blur-xl',
        'border-b border-border-default flex items-center justify-between px-3 md:px-6',
        'transition-all duration-300',
        sidebarCollapsed ? 'left-0 md:left-16' : 'left-0 md:left-56'
      )}
    >
      {/* Hamburger (mobile only) */}
      <button
        type="button"
        onClick={onMenuToggle}
        className="md:hidden flex items-center justify-center min-h-11 min-w-11 -ml-1 rounded-xl text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
        aria-label="Open navigation"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Spacer for left side */}
      <div className="flex-1" />

      {/* Actions - all on the right */}
      <div className="flex items-center gap-1.5 md:gap-3">
        {/* Quick note - capture a customer notebook note from anywhere */}
        <button
          type="button"
          onClick={() => setShowQuickNote(true)}
          className="flex items-center gap-2 px-2.5 md:px-3 py-2 rounded-xl text-sm text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
          title="Quick note"
          aria-label="Quick note"
        >
          <NotebookPen className="w-5 h-5" />
          <span className="hidden lg:inline">Quick note</span>
        </button>

        {/* Search - Command palette */}
        <GlobalSearch />

        {/* Theme toggle */}
        <ThemeToggle />

        {/* User menu */}
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-bg-tertiary transition-colors"
          >
            {isLoading ? (
              <div className="w-6 h-6 rounded-full bg-bg-tertiary animate-pulse" />
            ) : (
              <UserAvatar name={displayName} size="sm" />
            )}
            <div className="hidden sm:block text-left">
              <div className="text-sm font-medium text-text-primary">
                {displayName}
              </div>
              <div className="text-xs text-text-secondary capitalize">
                {userRole}
              </div>
            </div>
            <ChevronDown className="w-4 h-4 text-text-muted" />
          </button>

          {/* Dropdown menu */}
          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-64 bg-bg-secondary rounded-2xl shadow-lg border border-border-default py-2 z-50">
              <div className="px-4 py-3 border-b border-border-default">
                <p className="text-sm font-medium text-text-primary">{displayName}</p>
                <p className="text-xs text-text-secondary">{userEmail}</p>
              </div>

              <div className="py-1">
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate('/settings');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-tertiary"
                >
                  <User className="w-4 h-4" />
                  Profile Settings
                </button>
              </div>

              <div className="border-t border-border-default py-1">
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    logout();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>

    <QuickCaptureModal
      isOpen={showQuickNote}
      onClose={() => setShowQuickNote(false)}
      onSaved={(note) => navigate(`/notebook/${note.tenantId}`)}
    />
    </>
  );
}
